<script lang="ts">
	import classNames from 'classnames';
	import { onMount } from 'svelte';
	import { getAuthContext } from '../AuthDialogs/utils/getAuthContext/getAuthContext';
	import { Config } from '#lib/constants/config.js';
	import { ArtistCardTag } from '#lib/features/news/components/NewsCard/ArtistCardTag/index.js';
	import { GeoCardTag } from '#lib/features/news/components/NewsCard/GeoCardTag/index.js';
	import { HeniCardTag } from '#lib/features/news/components/NewsCard/HeniCardTag/index.js';
	import { getTagLink } from '#lib/features/news/utils/getTagLink/getTagLink.js';
	import type { SavedTags } from '#lib/websites/news/types.js';
	import { beforeNavigate } from '$app/navigation';
	import { page } from '$app/state';
	import { ChevronLeftIcon } from '$global/assets/icons/ChevronLeftIcon';
	import { HeartIcon } from '$global/assets/icons/HeartIcon';
	import { CircularProgress } from '$global/components/CircularProgress';
	import type { DialogStores } from '$global/components/Dialog';
	import { Drawer } from '$global/components/Drawer';
	import { Skeleton } from '$global/components/Skeleton';
	import { Tabs } from '$global/components/Tabs';
	import { Txt } from '$global/components/Txt';
	import { config } from '$global/config/heni-websites-config';
	import type { NavPopover } from '$global/features/navigation/utils/createNavPopover';
	import { SavedTagType } from '$gql/types-mutations';

	interface Props {
		popover: NavPopover;
		drawerStores: DialogStores;
		dataCy: string;
		forceShowGlobalNav?: boolean | undefined;
	}

	let {
		popover,
		drawerStores,
		dataCy,
		forceShowGlobalNav = undefined,
	}: Props = $props();

	// The URL param is the fallback authority: on error pages (and any route
	// whose load doesn't provide showGlobalNav) page.data is empty, and
	// app-mode (global_nav=0) visitors must stay on navless links.
	let showGlobalNav = $derived(
		forceShowGlobalNav ??
			page.data.showGlobalNav ??
			page.url.searchParams.get('global_nav') !== '0'
	);

	const {
		user,
		usersPrefs,
		removeUserTag,
		updatingUserTags,
		hasLoadedUserPrefs,
	} = getAuthContext();

	const handleClickClose = () => {
		drawerStores.states.open.set(false);
	};

	const handleClickTag = () => {
		popover.close();
		handleClickClose();
	};

	const handleClickArtistTag = (name: string) => {
		handleClickTag();
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(window.location as any) = `${Config.Domain}${getTagLink(showGlobalNav, 'artist', name)}`;
	};

	const handleClickGeoTag = (name: string) => {
		handleClickTag();
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(window.location as any) = `${Config.Domain}${getTagLink(showGlobalNav, 'geotag', name)}`;
	};

	const getHandleClickCross =
		(userPref: Parameters<typeof removeUserTag>[0]) => () => {
			if (userPref?.id) {
				removeUserTag(userPref);
			}
		};

	let activeTab = $state(0);

	onMount(() => {
		const mediaQuery = window.matchMedia(
			`(max-width: ${config.theme.screens.sm})`
		);

		const listener: Parameters<typeof mediaQuery.addEventListener>[1] = (e) => {
			if (!(e as Event & { matches: boolean }).matches) {
				drawerStores.states.open.set(false);
			}
		};

		mediaQuery.addEventListener('change', listener);
		return () => {
			mediaQuery.removeEventListener('change', listener);
		};
	});

	beforeNavigate(() => {
		drawerStores?.states.open.set(false);
	});
	let defaultTags = $derived(page.data.defaultTags);
	let dataCyPrefix = $derived(`${dataCy}-saved-tags`);
	let tagsToShow = $derived(
		($user ? $usersPrefs.savedTags : defaultTags) as NonNullable<SavedTags>
	);
	let showNoSavedTags = $derived(
		$hasLoadedUserPrefs &&
			!tagsToShow?.filter(
				(tag) =>
					tag?.type === (activeTab ? SavedTagType.Geotag : SavedTagType.Artist)
			)?.length
	);
</script>

<Drawer
	dataCy={dataCyPrefix}
	{drawerStores}
	classes={{ overlay: 'hidden' }}
	class="max-w-[100%]"
>
	<div class="relative h-full">
		<div
			class="bg-gray-0 flex h-[60px] items-center justify-between border-b border-b-1 border-b-gray-200 p-4"
		>
			<button
				onclick={() => {
					if (!$updatingUserTags) {
						handleClickClose();
					}
				}}
				class="absolute flex items-center gap-2 outline-none"
			>
				<ChevronLeftIcon class="h-4 w-4 translate-y-[1px]" />
				<Txt variant="h6">Saved Tags</Txt>
			</button>
		</div>

		<!-- Above the tabs rather than inside one: HENI is a fixed filter belonging
		to neither Artists nor Locations, and stays visible on both (HB-948). Its
		fixed 44px height is what `.fixed-wrapper` below offsets past. -->
		<div class="flex h-[44px] items-center border-b px-4">
			<HeniCardTag
				dataCy={dataCyPrefix}
				class="h-[24px] cursor-pointer"
				onClick={handleClickTag}
				{forceShowGlobalNav}
			/>
		</div>

		<Tabs
			classes={{
				container: 'mt-0',
				header: 'grid grid-cols-2 pr-0',
				button: 'first:border-r last:pl-0',
			}}
			disabled={!!$updatingUserTags}
			bind:activeTab
			dataCy={dataCyPrefix}
			tabs={[
				{
					id: 'artists',
					title: 'Artists',
				},
				{
					id: 'locations',
					title: 'Locations',
				},
			]}
		/>

		{#if $updatingUserTags}
			<div
				class="fixed left-0 z-10 flex h-[calc(100%-148px)] w-full bg-white opacity-70"
			></div>
			<div
				class="fixed left-0 z-20 flex h-[calc(100%-148px)] w-full items-center justify-center"
			>
				<CircularProgress {dataCy} class="h-8 w-8" />
			</div>
		{/if}

		<div class="fixed-wrapper px-4">
			{#if !$hasLoadedUserPrefs}
				<div class="mt-4 flex flex-wrap gap-2.5">
					{#each Array.from({ length: 10 }, (_, idx) => idx) as i (i)}
						<Skeleton
							class="h-[1.5rem] w-[6.5rem] rounded-md"
							variant="rectangular"
						/>
					{/each}
				</div>
			{:else if showNoSavedTags}
				<div class="flex min-h-[calc(100vh-158px)] flex-col justify-center">
					<div class="mb-2 flex w-full justify-center">
						<HeartIcon class="h-[3.5rem] w-[3.5rem]" color="gray-500" />
					</div>

					<Txt variant="label2" class="mx-auto mb-2 text-center">
						You have no saved {activeTab ? 'location' : 'artist'} tags
					</Txt>
					<Txt variant="body2" class="mx-auto text-center"
						>Lorem ipsum dolor sit amet, consectetur</Txt
					>
				</div>
			{:else}
				<div class="mt-4 flex flex-wrap gap-2.5">
					{#each tagsToShow as tag, i (tag?.id || `${tag?.type}-${tag?.name}`)}
						{#if !activeTab}
							{#if tag?.type === SavedTagType.Artist}
								<ArtistCardTag
									showNewVersion
									dataCy={dataCyPrefix}
									name={tag?.name}
									class={classNames(
										'h-full cursor-pointer !rounded-md !py-0.5',
										{
											'opacity-40': $updatingUserTags,
											'mb-4': i === tagsToShow.length - 1,
										}
									)}
									onClick={() => handleClickArtistTag(tag?.name)}
									showCross
									href={null}
									onClickCross={$user ? getHandleClickCross(tag) : undefined}
								/>
							{/if}
						{:else if tag?.type === SavedTagType.Geotag}
							<GeoCardTag
								showNewVersion
								dataCy={dataCyPrefix}
								name={tag?.name}
								onClick={() => handleClickGeoTag(tag?.name)}
								class={classNames('h-full cursor-pointer !rounded-md !py-0.5', {
									'opacity-40': $updatingUserTags,
									'mb-4': i === tagsToShow.length - 1,
								})}
								href={null}
								showCross
								onClickCross={$user ? getHandleClickCross(tag) : undefined}
							/>
						{/if}
					{/each}
				</div>
			{/if}
		</div>
	</div>
</Drawer>

<style>
	/* 114px of header + tabs, plus the 44px HENI tag row above them (HB-948). */
	.fixed-wrapper {
		width: 100%;
		position: fixed;
		top: 158px;
		left: 0px;
		transform: translate3d(0, 0, 0);
		overscroll-behavior: contain;
		overflow: scroll;
		height: calc(100dvh - 158px);
		-webkit-overflow-scrolling: touch;
	}
</style>
